// src/components/quickcreate/forms/QuickCreateClient.jsx
// Quick create a client inline — minimal fields only
// Full client management stays in ClientForm.js (SSOT)
//
// Props:
//   companyId   — string (from useCompany())
//   initialName — string (pre-fill client_name from typed text)
//   onCreated   — fn({ id, client_name, contact, email, company_id })
//   onCancel    — fn

import { useState } from "react";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";

export default function QuickCreateClient({ companyId, onCreated, onCancel, initialName = "" }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [form, setForm] = useState({
    client_name: initialName,
    contact:     "",
    email:       "",
  });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSave = async () => {
    // Validation
    if (!form.client_name.trim()) { setError("Client name is required"); return; }
    if (!form.contact.trim())     { setError("Contact number is required"); return; }

    setSaving(true);
    try {
      // Duplicate check — same logic as ClientForm.js
      const dupSnap = await getDocs(query(
        collection(db, "clients"),
        where("company_id",  "==", companyId),
        where("client_name", "==", form.client_name.trim())
      ));
      if (!dupSnap.empty) {
        setError(`Client "${form.client_name.trim()}" already exists`);
        setSaving(false);
        return;
      }

      // Create — exact ClientForm.js schema
      const docRef = await addDoc(collection(db, "clients"), {
        company_id:      companyId,
        client_name:     form.client_name.trim(),
        contact:         form.contact.trim(),
        email:           form.email.trim(),
        sites:           [],
        created_at:      serverTimestamp(),
        created_by:      user.uid || user.id || "",
        created_by_name: user.name || "",
      });

      onCreated({
        id:          docRef.id,
        client_name: form.client_name.trim(),
        contact:     form.contact.trim(),
        email:       form.email.trim(),
        company_id:  companyId,
      });

    } catch (err) {
      console.error("QuickCreateClient error:", err);
      setError(err.message || "Failed to create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">
        Minimum details only. Full profile can be edited in Client Master.
      </p>

      {/* client_name */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Client Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          placeholder="e.g. Tata Projects Ltd"
          value={form.client_name}
          onChange={e => set("client_name", e.target.value)}
          autoFocus
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300"
        />
      </div>

      {/* contact */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Contact Number <span className="text-red-400">*</span>
        </label>
        <input
          type="tel"
          placeholder="e.g. 9876543210"
          value={form.contact}
          onChange={e => set("contact", e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300"
        />
      </div>

      {/* email */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Email <span className="text-slate-300">(optional)</span>
        </label>
        <input
          type="email"
          placeholder="e.g. accounts@tata.com"
          value={form.email}
          onChange={e => set("email", e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300"
        />
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 font-medium hover:bg-slate-50 transition disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition disabled:opacity-40"
        >
          {saving ? "Saving..." : "Create Client"}
        </button>
      </div>
    </div>
  );
}
